import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../services/api';
import { initializeMockData } from '../mock';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  useEffect(() => {
    initializeMockData();
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const savedToken = localStorage.getItem('notimax_token');
    const savedUser = localStorage.getItem('notimax_user');
    
    if (savedToken && savedUser) {
      try {
        const user = JSON.parse(savedUser);
        // ALWAYS trust localStorage and keep user logged in
        setToken(savedToken);
        setCurrentUser(user);
        setLoading(false);
        
        // OPTIONAL: Sync with backend in background (non-blocking, non-critical)
        setTimeout(async () => {
          try {
            const response = await authAPI.getMe();
            // Update with fresh data silently (preferences, banner settings, etc.)
            const updatedUser = response.data;
            setCurrentUser(updatedUser);
            localStorage.setItem('notimax_user', JSON.stringify(updatedUser));
          } catch (error) {
            // CRITICAL: If backend fails, KEEP user logged in with cached data
            // Backend might be down temporarily, but user should stay authenticated
            console.warn('Backend sync failed, using cached session:', error.message);
          }
        }, 100);
        
        return; // User is authenticated from localStorage
      } catch (e) {
        console.error('Failed to parse saved user, but keeping session:', e);
        // Even if parsing fails, keep token and try to re-fetch from backend
        setToken(savedToken);
        setLoading(false);
        
        // Try to get user from backend - non-blocking
        setTimeout(async () => {
          try {
            const response = await authAPI.getMe();
            setCurrentUser(response.data);
            localStorage.setItem('notimax_user', JSON.stringify(response.data));
          } catch (error) {
            console.warn('Cannot restore user from backend, keeping cached session:', error);
            // DON'T logout - keep the session active with what we have
            // User can manually logout if needed
          }
        }, 100);
        return;
      }
    }
    
    // Only reach here if NO saved session exists
    setToken(null);
    setCurrentUser(null);
    setLoading(false);
  };

  const login = async (email, password) => {
    try {
      const response = await authAPI.login({ email, password });
      const { access_token, user } = response.data;
      
      localStorage.setItem('notimax_token', access_token);
      localStorage.setItem('notimax_user', JSON.stringify(user));
      setToken(access_token);
      setCurrentUser(user);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Error al iniciar sesión' 
      };
    }
  };

  const register = async (email, password, name) => {
    try {
      const response = await authAPI.register({ email, password, name });
      const { access_token, user } = response.data;
      
      localStorage.setItem('notimax_token', access_token);
      localStorage.setItem('notimax_user', JSON.stringify(user));
      setToken(access_token);
      setCurrentUser(user);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Error al registrarse' 
      };
    }
  };

  const logout = () => {
    setCurrentUser(null);
    setToken(null);
    localStorage.removeItem('notimax_token');
    localStorage.removeItem('notimax_user');
  };

  const value = {
    currentUser,
    token,
    login,
    register,
    logout,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
